/**
 * M11 Welle W3 — Geteilte Tenant-Scope-Single-Source-of-Truth (nicht React-gebunden).
 *
 * Dieses Modul ist der zentrale, nicht an React gebundene Zugriffspunkt auf den
 * aktiven Tenant-Scope. Es wird sowohl vom {@link ../context/AuthContext} (der den
 * resolved `effectiveScopeUid` hier spiegelt) als auch von Nicht-Hook-Modulen
 * genutzt — z.B. {@link ../api/apiRequest} (X-Tenant-Id-Header) und den
 * Workflow-Services, die `auth.currentUser.uid` direkt lesen.
 *
 * INERTHEITS-GARANTIE (oberste Direktive): Solange
 * `REACT_APP_TENANT_SCOPING_ENABLED` nicht exakt `'true'` ist, liefert
 * {@link getEffectiveScopeUid} immer `auth.currentUser.uid` und
 * {@link getTenantIdHeaderValue} immer `null` → bit-identisches Status-quo-Verhalten,
 * kein X-Tenant-Id-Header, kein fremder Scope.
 */
import { auth } from '../firebaseConfig';

/**
 * FE-Feature-Flag (CRA-Env, Default off). Wird einmalig beim Modul-Load gelesen.
 * `=== 'true'` fail-closed: jeder andere Wert (undefined, '', 'false') → deaktiviert.
 */
export const TENANT_SCOPING_ENABLED = process.env.REACT_APP_TENANT_SCOPING_ENABLED === 'true';

/** localStorage-Schlüssel für den zuletzt aktiven Tenant (Persistenz über Reloads). */
const ACTIVE_TENANT_STORAGE_KEY = 'activeTenantId';

/**
 * Vom AuthContext gespiegelter, resolved `effectiveScopeUid` für Nicht-Hook-Leser.
 * `null`, solange noch nicht gesetzt — der Getter fällt dann fail-safe auf die
 * eigene uid zurück.
 */
let mirroredEffectiveScopeUid: string | null = null;

/**
 * Vom AuthContext gespiegelter, gegen `availableTenants` VALIDIERTER aktiver
 * Tenant-Id-Header-Wert für Nicht-Hook-Leser ({@link getTenantIdHeaderValue}).
 *
 * `null` = eigener Tenant (kein Header). Wird NUR auf eine fremde Tenant-Id
 * gesetzt, wenn der AuthContext eine aktive Membership für sie validiert hat
 * (Spiegel von `activeTenant`, das auch `effectiveScopeUid` speist) → Scope und
 * Header referenzieren IMMER denselben Tenant. Roher/abgelaufener localStorage
 * schlägt damit nie auf den Header durch (W4 Carry-forward (f)).
 */
let mirroredActiveTenantIdHeader: string | null = null;

/**
 * Liest die persistierte aktive Tenant-Id aus localStorage.
 * @returns die gespeicherte Tenant-Id oder `null` (auch bei localStorage-Fehlern).
 */
export function readStoredActiveTenantId(): string | null {
  try {
    return window.localStorage.getItem(ACTIVE_TENANT_STORAGE_KEY);
  } catch {
    // localStorage kann in seltenen Fällen (Privatmodus/Quota) werfen — fail-safe.
    return null;
  }
}

/**
 * Persistiert die aktive Tenant-Id in localStorage. Fehler werden geschluckt
 * (Persistenz ist Best-Effort, nie Blocker).
 */
export function persistActiveTenantId(tenantId: string): void {
  try {
    window.localStorage.setItem(ACTIVE_TENANT_STORAGE_KEY, tenantId);
  } catch {
    /* Best-Effort — bewusst ignoriert. */
  }
}

/** Entfernt die persistierte aktive Tenant-Id (z.B. bei Logout). */
export function clearStoredActiveTenantId(): void {
  try {
    window.localStorage.removeItem(ACTIVE_TENANT_STORAGE_KEY);
  } catch {
    /* Best-Effort — bewusst ignoriert. */
  }
}

/**
 * Spiegelt den vom AuthContext resolved `effectiveScopeUid`, damit Nicht-Hook-Module
 * (Services, apiRequest) denselben Scope-Wert sehen wie React-Consumer.
 * @param uid resolved effectiveScopeUid, oder `null` zum Zurücksetzen (Logout).
 */
export function setEffectiveScopeUid(uid: string | null): void {
  mirroredEffectiveScopeUid = uid;
}

/**
 * Spiegelt den vom AuthContext gegen `availableTenants` validierten aktiven
 * Tenant-Id-Header-Wert. Nur so erreicht eine fremde Tenant-Id den
 * `X-Tenant-Id`-Header — ein roher localStorage-Wert ohne aktive Membership
 * tut es NICHT (W4 Carry-forward (f)).
 * @param tenantId validierte fremde Tenant-Id, oder `null` für eigenen Tenant /
 *                 Reset (Logout, Flag off).
 */
export function setTenantIdHeaderValue(tenantId: string | null): void {
  mirroredActiveTenantIdHeader = tenantId;
}

/**
 * Zentraler Scope-uid-Getter für Nicht-Hook-Module (Workflow-Services etc.).
 *
 * - Flag off ODER kein gespiegelter Scope → `auth.currentUser?.uid ?? ''`
 *   (bit-identisch zum bisherigen `auth.currentUser.uid`-Zugriff).
 * - Flag on + gespiegelter Scope → der aktive Tenant-`scopeUid`.
 *
 * @returns die Scope-uid für Daten-Queries, oder `''` wenn nicht eingeloggt.
 */
export function getEffectiveScopeUid(): string {
  if (TENANT_SCOPING_ENABLED && mirroredEffectiveScopeUid) {
    return mirroredEffectiveScopeUid;
  }
  return auth.currentUser?.uid ?? '';
}

/**
 * Liefert den Wert für den `X-Tenant-Id`-Request-Header.
 *
 * Liest NICHT den rohen localStorage-Wert, sondern den vom AuthContext gegen
 * `availableTenants` (status='active') VALIDIERTEN, gespiegelten Wert
 * ({@link setTenantIdHeaderValue}). Damit referenzieren FE-Scope
 * ({@link getEffectiveScopeUid}) und Header IMMER denselben Tenant: ein
 * veralteter/manipulierter localStorage-Eintrag ohne aktive Membership führt zu
 * keinem Header (statt einem Header-/Scope-Mismatch). (W4 Carry-forward (f)).
 *
 * @returns die validierte aktive Tenant-Id, wenn das Flag on ist und ein fremder
 *          Tenant validiert aktiv ist; sonst `null` (→ Header wird weggelassen →
 *          PHP-Provider behandelt fehlenden Header fail-safe als eigenen Tenant).
 */
export function getTenantIdHeaderValue(): string | null {
  if (!TENANT_SCOPING_ENABLED) {
    return null;
  }
  return mirroredActiveTenantIdHeader;
}
